import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  Props,
  State,
  Pattern,
  Cursor,
  StateFormatterProps,
  Size,
} from '@app/components/Input/types';

const emptyFn = () => {};

export const useInput = ({
  value = '',
  cursor: customCursor,
  disabled = false,
  pattern = 'text',
  onBlur = emptyFn,
  onFocus = emptyFn,
  status = 'default',
  size = 'normal',
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [hover, setHover] = useState(false);
  const [fill, setFill] = useState(false);
  const [focus, setFocus] = useState(false);
  const [state, setState] = useState<State>('default');

  const onMouseEnter = () => setHover(true);
  const onMouseLeave = () => setHover(false);

  const getCursor = (pattern: Pattern, disabled: boolean): Cursor => {
    if (disabled) return 'default';
    switch (pattern) {
      case 'text':
        return 'text';
      default:
        return 'default';
    }
  };

  const getPadding = (size: Size) => {
    switch (size) {
      case 'small':
        return '4px 6px';
      case 'normal':
        return '7px 11px';
      case 'large':
        return '10px 9px';
      default:
        return '7px 9px';
    }
  };

  const cursor = useMemo(
    () => customCursor || getCursor(pattern, disabled),
    [customCursor, pattern, disabled]
  );

  const padding = useMemo(() => getPadding(size), [size]);

  const stateFormatter = ({
    fill,
    hover,
    focus,
    disabled,
    error,
  }: StateFormatterProps): State => {
    if (disabled) return 'disabled';
    if (error) return 'error';
    if (hover) return 'hover';
    if (focus) return 'focus';
    if (fill) return 'fill';

    return 'default';
  };
  const handleFocus = () => {
    if (disabled) return;
    inputRef.current?.focus();
    setFocus(true);
    onFocus();
  };

  const handleBlur = () => {
    inputRef.current?.blur();
    setFocus(false);
    onBlur();
  };

  const handleState = useCallback(
    () =>
      setState(
        stateFormatter({
          hover,
          fill,
          focus,
          disabled,
          error: status === 'error',
        })
      ),
    [hover, fill, focus, disabled, status]
  );

  useEffect(() => {
    const ref = inputRef.current;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Enter' && e.target instanceof HTMLInputElement) {
        e.target.blur();
      }
    };

    ref?.addEventListener('keydown', handleKeyPress, false);

    return () => ref?.removeEventListener('keydown', handleKeyPress, false);
  }, []);

  useLayoutEffect(() => {
    setFill(Boolean(value));
  }, [value]);

  useLayoutEffect(() => {
    handleState();
  }, [handleState]);

  return {
    cursor,
    state,
    onMouseEnter,
    onMouseLeave,
    handleFocus,
    handleBlur,
    inputRef,
    padding,
  };
};
